/**
 * Externalized filter-operator definitions.
 *
 * Each operator describes the `condition` string that the DataTable
 * expects inside a filter condition entry
 * (`{ condition: string; filterValue: unknown }`).
 *
 * To add a custom operator, create a new `FilterOperator` object and
 * pass it via the `fieldOperators` or `defaultOperator` options of
 * `useFormGridFilterSync` — **no modification to the hook is required**.
 *
 * @example
 * ```ts
 * // Built-in usage
 * import { FilterOperators } from './FilterOperators';
 * // → FilterOperators.contains, FilterOperators.equals, …
 *
 * // Custom operator
 * const myCustomOp: FilterOperator = { condition: 'myCustomCondition' };
 * ```
 */

// ── Types ──────────────────────────────────────────────────────────────

/**
 * Describes a single filter operator that can be applied to a
 * DataTable column filter.
 */
export interface FilterOperator {
  /** The condition string recognized by the DataTable (e.g. `'contains'`, `'is'`). */
  readonly condition: string;
}

// ── Built-in operators ─────────────────────────────────────────────────

/**
 * Registry of built-in filter operators.
 *
 * Extend the set by creating your own `FilterOperator` objects — the
 * hook accepts any object that satisfies the interface.
 */
export const FilterOperators: Readonly<Record<string, FilterOperator>> & {
  readonly contains: FilterOperator;
  readonly equals: FilterOperator;
  readonly notequals: FilterOperator;
  readonly greaterThan: FilterOperator;
  readonly greaterThanOrEqual: FilterOperator;
  readonly lessThan: FilterOperator;
  readonly lessThanOrEqual: FilterOperator;
  readonly startsWith: FilterOperator;
  readonly endsWith: FilterOperator;
} = {
  contains: { condition: "contains" },
  equals: { condition: "equals" },
  notequals: { condition: "not-equals" },
  greaterThan: { condition: "greater" },
  greaterThanOrEqual: { condition: "greater-equal" },
  lessThan: { condition: "less" },
  lessThanOrEqual: { condition: "less-equal" },
  startsWith: { condition: "starts-with" },
  endsWith: { condition: "ends-with" },
};
